"use client";

import { useRef } from "react";
import { useInView, useReducedMotion } from "framer-motion";

export type TransitionType = "FadeIn" | "SlideIn" | "SlideUp" | "ScaleIn" | "BlurIn";

interface Props {
	children: React.ReactNode | React.ReactNode[];
	type?: TransitionType;
	delay?: number;
	duration?: number;
}

const variantFor = (type: TransitionType, inView: boolean) => {
	switch (type) {
		case "FadeIn":
			return { opacity: inView ? 1 : 0 };
		case "SlideIn":
			return {
				opacity: inView ? 1 : 0,
				transform: inView ? "translateY(0)" : "translateY(100px)",
			};
		case "SlideUp":
			return {
				opacity: inView ? 1 : 0,
				transform: inView ? "translateY(0)" : "translateY(40px)",
			};
		case "ScaleIn":
			return {
				opacity: inView ? 1 : 0,
				transform: inView ? "scale(1)" : "scale(0.94)",
			};
		case "BlurIn":
			return {
				opacity: inView ? 1 : 0,
				filter: inView ? "blur(0)" : "blur(12px)",
			};
	}
};

const Transition = ({ children, type = "SlideIn", delay = 0, duration = 0.8 }: Props) => {
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
	const shouldReduceMotion = useReducedMotion();

	const inView = shouldReduceMotion ? true : isInView;
	const ease = "cubic-bezier(0.17, 0.55, 0.55, 1)";
	const transition = shouldReduceMotion
		? "none"
		: `opacity ${duration}s ${ease} ${delay}s, transform ${duration}s ${ease} ${delay}s, filter ${duration}s ${ease} ${delay}s`;

	return (
		<div ref={ref} style={{ ...variantFor(type, inView), transition }}>
			{children}
		</div>
	);
};

export default Transition;
